"use client";

import type { ReactNode } from "react";
import { TimingCitySelector, type SavedCity } from "@/components/choghadiya/ChoghadiyaCitySelector";
import type { MuhuratPurpose, ShubhMuhuratResponse } from "@/lib/api/muhurat";
import { ShubhMuhuratDigest } from "./ShubhMuhuratDigest";
import type { MuhuratVerdict } from "./shubh-muhurat-verdict";
import { useShubhMuhurat } from "./use-shubh-muhurat";

// SEO_AGENT_BRIDGE_SKIP: timing answer surfaces carry no inline offer since the
// 2026-10-03 offer consolidation (the timing report bridge: 1,193 clients shown,
// 0 orders); the root layout's GlobalAgentEntry is their one chat entry.

type ToolLocale = "en" | "hi";

const PURPOSE_OPTIONS: Array<{ value: MuhuratPurpose; label: string; label_hi: string }> = [
  { value: "general", label: "Important beginning", label_hi: "कोई महत्वपूर्ण शुरुआत" },
  { value: "career", label: "Career or job joining", label_hi: "नौकरी या करियर की शुरुआत" },
  { value: "business", label: "Business or signing", label_hi: "व्यापार या अनुबंध" },
  { value: "marriage", label: "Marriage or engagement", label_hi: "विवाह या सगाई" },
  { value: "travel", label: "Travel or departure", label_hi: "यात्रा या प्रस्थान" },
  { value: "meditation", label: "Meditation or puja", label_hi: "ध्यान या पूजा" },
];

const TOOL_COPY = {
  en: {
    eyebrow: "Live India timing check",
    title: "Can I start something important now?",
    intro:
      "Choose an Indian city. The result uses today's local sunrise, Panchang and Choghadiya windows—not a fixed national timetable.",
    purpose: "What are you starting?",
    tones: { good: "Good time now", wait: "Avoid window active", neutral: "Timing check" },
    calculating: "Calculating today's shubh muhurat",
    retry: "Try again",
    checking: "Checking the current India time…",
    error: null,
  },
  hi: {
    eyebrow: "भारत के शहरों के लिए लाइव समय जांच",
    title: "क्या अभी कोई महत्वपूर्ण काम शुरू करना ठीक है?",
    intro:
      "भारत का कोई शहर चुनें। परिणाम उस शहर के आज के सूर्योदय, पंचांग और चौघड़िया पर आधारित है — पूरे देश के लिए एक तय समय-सारणी पर नहीं।",
    purpose: "आप क्या शुरू कर रहे हैं?",
    tones: { good: "अभी शुभ समय", wait: "वर्जित समय चल रहा है", neutral: "समय की जांच" },
    calculating: "आज के शुभ मुहूर्त की गणना हो रही है",
    retry: "दोबारा प्रयास करें",
    checking: "भारत का वर्तमान समय जांचा जा रहा है…",
    error: "मुहूर्त की गणना अभी लोड नहीं हो सकी। कृपया दोबारा प्रयास करें।",
  },
} as const;

function PurposePicker({
  purpose,
  onChange,
  locale,
}: {
  purpose: MuhuratPurpose;
  onChange: (purpose: MuhuratPurpose) => void;
  locale: ToolLocale;
}) {
  return (
    <label className="mx-auto mt-4 block max-w-sm text-left">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ish-text-muted">
        {TOOL_COPY[locale].purpose}
      </span>
      <select
        value={purpose}
        onChange={(event) => onChange(event.target.value as MuhuratPurpose)}
        className="input-sacred w-full rounded-lg px-3 py-2 text-sm text-ish-dark"
      >
        {PURPOSE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {locale === "hi" ? option.label_hi : option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function VerdictCard({ verdict, locale }: { verdict: MuhuratVerdict; locale: ToolLocale }) {
  const styles = {
    good: "border-emerald-200 bg-emerald-50/70 text-emerald-900",
    wait: "border-amber-200 bg-amber-50/70 text-amber-950",
    neutral: "border-ish-border-light bg-ish-surface text-ish-dark",
  } as const;
  const labels = TOOL_COPY[locale].tones;

  return (
    <div className={`rounded-xl border p-4 ${styles[verdict.tone]}`} aria-live="polite">
      <p className="text-xs font-semibold uppercase tracking-widest">{labels[verdict.tone]}</p>
      <p className="mt-1 text-lg font-bold">{verdict.heading}</p>
      <p className="mt-1 text-sm leading-relaxed opacity-80">{verdict.detail}</p>
    </div>
  );
}

// Server HTML shows the skeleton only when the page's IST-day digest seed
// failed, so it is marked degraded: the public HTML cache never stores it and
// the live correctness check reports it.
function ToolSkeleton({ locale }: { locale: ToolLocale }) {
  return (
    <div
      className="space-y-3"
      aria-label={TOOL_COPY[locale].calculating}
      data-ishvaram-degraded="shubh-muhurat-seed"
    >
      <div className="h-28 animate-pulse rounded-xl bg-stone-200/70" />
      <div className="h-44 animate-pulse rounded-xl bg-stone-200/70" />
    </div>
  );
}

export default function ShubhMuhuratTool({
  initialDigest,
  initialPreviousNightDigest = null,
  initialNowMinute = null,
  locale = "en",
  initialCity,
  citySelector,
}: {
  initialDigest: ShubhMuhuratResponse | null;
  initialPreviousNightDigest?: ShubhMuhuratResponse | null;
  /** The server's current India minute, so the verdict renders in the HTML. */
  initialNowMinute?: number | null;
  locale?: ToolLocale;
  /** The city the server rendered (`?city=` hubs). */
  initialCity?: SavedCity;
  /**
   * URL-addressed hubs pass their `?city=` selector form; it replaces the
   * in-page city search so the result always matches the URL.
   */
  citySelector?: ReactNode;
}) {
  const { city, digest, error, loading, purpose, retry, selectCity, selectPurpose, verdict } =
    useShubhMuhurat(
      initialDigest,
      initialPreviousNightDigest,
      initialNowMinute,
      locale,
      initialCity
    );
  const copy = TOOL_COPY[locale];

  return (
    <section className="intent-interface-timing-first min-h-[calc(100vh-130px)]">
      <div className="card-sacred p-5 sm:p-6">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-intent-brand">
            {copy.eyebrow}
          </p>
          <h2 className="mt-2 font-serif text-2xl font-bold text-ish-dark">{copy.title}</h2>
          <p className="mt-2 text-sm leading-relaxed text-ish-text-muted">{copy.intro}</p>
          {citySelector ?? (
            <TimingCitySelector
              city={city}
              onCitySelect={selectCity}
              countryFilter="IN"
              locale={locale}
            />
          )}
          <PurposePicker purpose={purpose} onChange={selectPurpose} locale={locale} />
        </div>

        <div className="mt-5">
          {loading && <ToolSkeleton locale={locale} />}
          {error && !loading && (
            <div className="rounded-xl border border-red-200 bg-red-50/70 p-5 text-center">
              <p className="text-sm text-red-700">{copy.error ?? error}</p>
              <button
                type="button"
                onClick={retry}
                className="mt-3 text-sm font-semibold text-intent-brand"
              >
                {copy.retry}
              </button>
            </div>
          )}
          {digest && !loading && !error && (
            <div className="space-y-5">
              {verdict ? (
                <VerdictCard verdict={verdict} locale={locale} />
              ) : (
                <p className="text-center text-sm text-ish-text-muted">{copy.checking}</p>
              )}
              <ShubhMuhuratDigest digest={digest} locale={locale} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
